import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { usePartnerDocuments } from '@/hooks/usePartnerDocuments';
import { DocumentVerificationDialog } from './DocumentVerificationDialog';
import { FileText, Download, Edit, Trash2, MoreVertical, AlertCircle, CheckCircle, XCircle, Clock } from 'lucide-react';
import { format, differenceInDays, parseISO } from 'date-fns';

interface DocumentListProps {
  companyId: string;
  isContractorView?: boolean;
  contractorUserId?: string;
}

export function DocumentList({ companyId, isContractorView = false, contractorUserId }: DocumentListProps) {
  const { documents, isLoading, downloadDocument, deleteDocument, verifyDocument } = usePartnerDocuments(companyId);
  const [verifyingDocId, setVerifyingDocId] = useState<string | null>(null);

  const canDeleteDocument = (doc: any) => {
    // Contractors can only delete their own uploads
    if (isContractorView && contractorUserId) {
      return doc.uploaded_by_contractor_user_id === contractorUserId;
    }
    // Tenant admins can delete any document
    return true;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'verified':
        return <Badge className="bg-success text-success-foreground"><CheckCircle className="h-3 w-3 mr-1" />Verified</Badge>;
      case 'rejected':
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
      case 'expired':
        return <Badge variant="destructive"><AlertCircle className="h-3 w-3 mr-1" />Expired</Badge>;
      default:
        return <Badge className="bg-orange-500 hover:bg-orange-600 text-white"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
    }
  };

  const getExpiryWarning = (expiryDate: string | undefined) => {
    if (!expiryDate) return null;
    
    const daysUntilExpiry = differenceInDays(parseISO(expiryDate), new Date());
    
    if (daysUntilExpiry < 0) {
      return <Badge variant="destructive">Expired</Badge>;
    } else if (daysUntilExpiry <= 7) {
      return <Badge variant="destructive">Expires in {daysUntilExpiry} days</Badge>;
    } else if (daysUntilExpiry <= 30) {
      return <Badge className="bg-warning text-warning-foreground">Expires in {daysUntilExpiry} days</Badge>;
    }
    
    return null;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const groupedDocuments = documents.reduce((acc, doc) => {
    const category = doc.document_category || 'other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(doc);
    return acc;
  }, {} as Record<string, typeof documents>);

  if (isLoading) {
    return <div className="text-center py-8 text-muted-foreground">Loading documents...</div>;
  }

  if (documents.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No documents uploaded yet</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {Object.entries(groupedDocuments).map(([category, docs]) => (
        <Card key={category}>
          <CardHeader>
            <CardTitle className="text-lg capitalize">{category}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {docs.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <FileText className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium truncate">{doc.document_type}</p>
                        {doc.is_required_for_prequalification && (
                          <Badge variant="outline" className="text-xs">Required</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground truncate">{doc.file_name}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                        <span>{formatFileSize(doc.file_size_bytes)}</span>
                        {doc.expiry_date && (
                          <>
                            <span>•</span>
                            <span>Expires: {format(parseISO(doc.expiry_date), 'MMM d, yyyy')}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {getExpiryWarning(doc.expiry_date)}
                    {getStatusBadge(doc.verification_status)}
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => downloadDocument(doc.id)}>
                          <Download className="h-4 w-4 mr-2" />
                          Download
                        </DropdownMenuItem>
                        {!isContractorView && doc.verification_status === 'pending' && (
                          <DropdownMenuItem onClick={() => setVerifyingDocId(doc.id)}>
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Verify
                          </DropdownMenuItem>
                        )}
                        {canDeleteDocument(doc) && (
                          <DropdownMenuItem 
                            onClick={() => deleteDocument(doc.id)}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}

      {verifyingDocId && (
        <DocumentVerificationDialog
          open={!!verifyingDocId}
          onOpenChange={(open) => !open && setVerifyingDocId(null)}
          documentId={verifyingDocId}
          companyId={companyId}
          document={documents.find(d => d.id === verifyingDocId)}
          onVerify={verifyDocument}
        />
      )}
    </div>
  );
}
